import { Icon } from './Icon'
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

type Appearance = 'light' | 'dark' | 'system'
const STORAGE_KEY = 'seldon.appearance'
const isAppearance = (value: unknown): value is Appearance =>
  value === 'light' || value === 'dark' || value === 'system'
const ThemeContext = createContext<{
  appearance: Appearance
  changeAppearance: (value: Appearance) => void
} | null>(null)

function apply(appearance: Appearance) {
  const theme =
    appearance === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : appearance
  document.documentElement.dataset.theme = theme
  document.documentElement.dataset.appearance = appearance
  document.documentElement.style.colorScheme = theme
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [appearance, setAppearance] = useState<Appearance>(() => {
    const initial = document.documentElement.dataset.appearance
    return isAppearance(initial) ? initial : 'light'
  })
  useEffect(() => {
    apply(appearance)
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onSystemChange = () => {
      if (appearance === 'system') apply(appearance)
    }
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY && event.key !== null) return
      const next = isAppearance(event.newValue) ? event.newValue : 'light'
      apply(next)
      setAppearance(next)
    }
    media.addEventListener('change', onSystemChange)
    window.addEventListener('storage', onStorage)
    return () => {
      media.removeEventListener('change', onSystemChange)
      window.removeEventListener('storage', onStorage)
    }
  }, [appearance])
  function changeAppearance(value: Appearance) {
    apply(value)
    setAppearance(value)
    try {
      localStorage.setItem(STORAGE_KEY, value)
    } catch {
      /* Keep the choice for this session. */
    }
  }
  return (
    <ThemeContext.Provider value={{ appearance, changeAppearance }}>{children}</ThemeContext.Provider>
  )
}

export function ThemeSelector({ sidebar = false }: { sidebar?: boolean }) {
  const theme = useContext(ThemeContext)
  if (!theme) throw new Error('ThemeSelector requires ThemeProvider')
  return (
    <div className={`appearance-control${sidebar ? ' sidebar-appearance' : ''}`}>
      <div className="appearance-options" role="group" aria-label="Appearance">
        {(['light', 'dark', 'system'] as const).map((value) => (
          <button
            key={value}
            type="button"
            aria-label={value[0].toUpperCase() + value.slice(1)}
            title={sidebar ? value[0].toUpperCase() + value.slice(1) : undefined}
            aria-pressed={theme.appearance === value}
            onClick={() => theme.changeAppearance(value)}
          >
            {sidebar && (
              <span className="appearance-icon">
                <Icon
                  name={value === 'light' ? 'sun' : value === 'dark' ? 'moon' : 'monitor'}
                  size={17}
                />
              </span>
            )}
            <span className="appearance-option-label">{value[0].toUpperCase() + value.slice(1)}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
